import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Database, ArrowLeft, Play, Plus, X, ShieldAlert, AlertTriangle, RefreshCw, Info,
} from "lucide-react";

const OP_LABELS = {
  equals: "=", not_equals: "≠", greater_than: ">", greater_than_or_equal: "≥",
  less_than: "<", less_than_or_equal: "≤", contains: "contains", starts_with: "starts with",
  in: "in (comma list)", not_in: "not in (comma list)", between: "between (a,b)",
  date_before: "before", date_after: "after", date_between: "between (a,b)",
  is_null: "is empty", is_not_null: "is not empty",
};
const LIST_OPS = ["in", "not_in", "between", "date_between"];
const NO_VALUE_OPS = ["is_null", "is_not_null"];

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return <span className="text-slate-600">—</span>;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return v.toLocaleString("en-IN");
  return String(v);
};

export default function DataExplorer() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [datasets, setDatasets] = useState([]);
  const [dataset, setDataset] = useState(sp.get("dataset") || "customers");
  const [schema, setSchema] = useState(null);
  const [cols, setCols] = useState([]);
  const [filters, setFilters] = useState([]);
  const [sortField, setSortField] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [tenantId, setTenantId] = useState(sp.get("tenant_id") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/platform/explorer/datasets").then((r) => setDatasets(r.data.datasets)).catch(() => {});
  }, []);

  const loadSchema = useCallback((ds) => {
    setSchema(null); setCols([]); setFilters([]); setSortField(""); setResult(null); setError(null);
    api.get(`/platform/explorer/schema/${ds}`).then((r) => {
      setSchema(r.data);
      setCols(r.data.columns.filter((c) => !c.pii).slice(0, 6).map((c) => c.column));
    }).catch((e) => setError(e));
  }, []);
  useEffect(() => { loadSchema(dataset); }, [dataset, loadSchema]);

  const piiSelected = schema ? cols.some((c) => schema.columns.find((x) => x.column === c)?.pii) : false;
  const colMeta = (name) => schema?.columns.find((c) => c.column === name);

  const toggleCol = (c) => setCols((p) => p.includes(c) ? p.filter((x) => x !== c) : [...p, c]);
  const addFilter = () => {
    const f = schema.columns.find((c) => c.filterable);
    if (f) setFilters((p) => [...p, { field: f.column, operator: f.operators[0], value: "" }]);
  };
  const updateFilter = (i, patch) => setFilters((p) => p.map((f, j) => j === i ? { ...f, ...patch } : f));
  const removeFilter = (i) => setFilters((p) => p.filter((_, j) => j !== i));

  const run = async (toPage = 1) => {
    setLoading(true); setError(null);
    const payload = {
      dataset, columns: cols, page: toPage, page_size: Number(pageSize),
      tenant_id: tenantId || "",
      filters: filters.map((f) => {
        let value = f.value;
        if (NO_VALUE_OPS.includes(f.operator)) value = null;
        else if (LIST_OPS.includes(f.operator)) value = String(f.value).split(",").map((s) => s.trim()).filter(Boolean).map((s) => isNaN(Number(s)) ? s : Number(s));
        else if (colMeta(f.field)?.type === "number") value = Number(f.value);
        else if (colMeta(f.field)?.type === "bool") value = f.value === "true" || f.value === true;
        return { field: f.field, operator: f.operator, value };
      }),
      sort: sortField ? { field: sortField, direction: sortDir } : null,
    };
    try {
      const r = await api.post("/platform/explorer/query", payload);
      setResult(r.data); setPage(toPage);
    } catch (e) { setError(e); toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const clearFilters = () => { setFilters([]); setSortField(""); };
  const sortableCols = schema?.columns.filter((c) => c.sortable) || [];

  return (
    <div data-testid="dx-explorer" className="text-slate-100">
      <button onClick={() => navigate("/platform-admin")} className="mb-3 flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft className="h-4 w-4" /> Platform</button>
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-lg bg-indigo-500/15 grid place-items-center"><Database className="h-5 w-5 text-indigo-400" /></div>
        <div><h1 className="font-display text-2xl font-bold">Data Explorer</h1>
          <p className="text-xs text-slate-500">Read-only · allowlisted datasets & columns · Preview</p></div>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[320px_1fr]">
        {/* query builder */}
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Dataset</label>
            <select data-testid="dx-dataset" value={dataset} onChange={(e) => setDataset(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm">
              {datasets.map((d) => <option key={d.dataset} value={d.dataset}>{d.name}</option>)}
            </select>
            {schema && <p className="mt-2 text-xs text-slate-500">{schema.description}</p>}
            <input data-testid="dx-tenant" value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="Filter by business/tenant ID (optional)" className="mt-3 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-xs" />
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
            <div className="flex items-center justify-between"><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Columns</label><span className="text-xs text-slate-500">{cols.length} selected</span></div>
            <div className="mt-2 max-h-56 space-y-1 overflow-y-auto pr-1" data-testid="dx-columns">
              {schema?.columns.map((c) => (
                <label key={c.column} className="flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-slate-800/50">
                  <input type="checkbox" data-testid={`dx-col-${c.column}`} checked={cols.includes(c.column)} onChange={() => toggleCol(c.column)} className="accent-indigo-500" />
                  <span className={cols.includes(c.column) ? "text-slate-100" : "text-slate-400"}>{c.column}</span>
                  {c.pii && <span data-testid={`dx-pii-tag-${c.column}`} className="rounded bg-rose-500/15 px-1.5 text-[10px] font-semibold text-rose-300">PII</span>}
                  {c.derived && <span className="rounded bg-slate-700 px-1.5 text-[10px] text-slate-300">derived</span>}
                </label>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
            <div className="flex items-center justify-between"><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Filters</label>
              <button data-testid="dx-add-filter" onClick={addFilter} className="flex items-center gap-1 text-xs text-indigo-300 hover:text-indigo-200"><Plus className="h-3.5 w-3.5" /> Add</button></div>
            <div className="mt-2 space-y-2" data-testid="dx-filters">
              {filters.map((f, i) => {
                const meta = colMeta(f.field);
                return (
                  <div key={i} className="rounded-lg border border-slate-800 bg-slate-950 p-2">
                    <div className="flex items-center gap-1">
                      <select data-testid={`dx-filter-field-${i}`} value={f.field} onChange={(e) => { const nm = colMeta(e.target.value); updateFilter(i, { field: e.target.value, operator: nm.operators[0], value: "" }); }} className="flex-1 rounded border border-slate-800 bg-slate-900 px-2 py-1 text-xs">
                        {schema.columns.filter((c) => c.filterable).map((c) => <option key={c.column} value={c.column}>{c.column}</option>)}
                      </select>
                      <select data-testid={`dx-filter-op-${i}`} value={f.operator} onChange={(e) => updateFilter(i, { operator: e.target.value })} className="rounded border border-slate-800 bg-slate-900 px-2 py-1 text-xs">
                        {(meta?.operators || []).map((o) => <option key={o} value={o}>{OP_LABELS[o] || o}</option>)}
                      </select>
                      <button onClick={() => removeFilter(i)} className="text-slate-500 hover:text-rose-300"><X className="h-3.5 w-3.5" /></button>
                    </div>
                    {!NO_VALUE_OPS.includes(f.operator) && (
                      <input data-testid={`dx-filter-value-${i}`} type={meta?.type === "number" ? "number" : meta?.type === "date" ? "text" : "text"}
                        value={f.value} onChange={(e) => updateFilter(i, { value: e.target.value })}
                        placeholder={LIST_OPS.includes(f.operator) ? "comma,separated" : meta?.type === "date" ? "YYYY-MM-DD…" : "value"}
                        className="mt-1 w-full rounded border border-slate-800 bg-slate-900 px-2 py-1 text-xs" />
                    )}
                  </div>
                );
              })}
              {!filters.length && <div className="text-xs text-slate-600">No filters</div>}
            </div>
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4 space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sort</label>
            <div className="flex gap-2">
              <select data-testid="dx-sort-field" value={sortField} onChange={(e) => setSortField(e.target.value)} className="flex-1 rounded-lg border border-slate-800 bg-slate-950 px-2 py-1.5 text-xs">
                <option value="">Default</option>{sortableCols.map((c) => <option key={c.column} value={c.column}>{c.column}</option>)}
              </select>
              <select data-testid="dx-sort-dir" value={sortDir} onChange={(e) => setSortDir(e.target.value)} className="rounded-lg border border-slate-800 bg-slate-950 px-2 py-1.5 text-xs">
                <option value="desc">Desc</option><option value="asc">Asc</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">Page size</label>
              <select data-testid="dx-page-size" value={pageSize} onChange={(e) => setPageSize(e.target.value)} className="rounded-lg border border-slate-800 bg-slate-950 px-2 py-1.5 text-xs">
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          {piiSelected && (
            <div data-testid="dx-pii-warning" className="flex items-start gap-2 rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-200">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> PII columns selected. This query is audited (columns & filters only — never the values).
            </div>
          )}
          <div className="flex gap-2">
            <button data-testid="dx-run" disabled={loading || !cols.length} onClick={() => run(1)} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-indigo-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-indigo-400 disabled:opacity-50">
              <Play className="h-4 w-4" /> {loading ? "Running…" : "Run query"}
            </button>
            <button data-testid="dx-clear" onClick={clearFilters} className="rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800">Clear</button>
          </div>
        </div>

        {/* results */}
        <div>
          {error ? (
            <div data-testid="dx-error" className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-6 text-center">
              <AlertTriangle className="mx-auto h-6 w-6 text-rose-400" />
              <div className="mt-2 font-medium text-rose-200">Query failed</div>
              <div className="mt-1 text-sm text-slate-400">{apiErr(error)}</div>
              <button data-testid="dx-retry" onClick={() => run(page)} className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800"><RefreshCw className="h-4 w-4" /> Retry</button>
            </div>
          ) : loading ? <div data-testid="dx-loading" className="py-16 text-center text-slate-400">Running query…</div>
          : !result ? (
            <div data-testid="dx-idle" className="rounded-xl border border-dashed border-slate-700 bg-slate-900/40 p-10 text-center">
              <Info className="mx-auto h-6 w-6 text-slate-500" />
              <div className="mt-2 text-slate-300">Choose columns and filters, then Run query</div>
            </div>
          ) : !result.rows.length ? (
            <div data-testid="dx-empty" className="rounded-xl border border-dashed border-slate-700 bg-slate-900/40 p-10 text-center">
              <Info className="mx-auto h-6 w-6 text-slate-500" />
              <div className="mt-2 text-slate-300">No rows match this query</div>
            </div>
          ) : (
            <div data-testid="dx-results">
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-400">
                <span data-testid="dx-count">Showing {(page - 1) * result.page_size + 1}–{(page - 1) * result.page_size + result.rows.length}{result.total_count != null ? ` of ${result.total_count.toLocaleString("en-IN")}` : ""}</span>
                {result.pii_included && <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-xs font-semibold text-rose-300">PII included · audited</span>}
              </div>
              <div className="mt-2 overflow-x-auto rounded-xl border border-slate-800">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-500">
                    <tr>{result.columns.map((c) => <th key={c} className="px-3 py-2.5 font-medium whitespace-nowrap">{c}{colMeta(c)?.pii && <span className="ml-1 text-rose-400">•</span>}</th>)}</tr>
                  </thead>
                  <tbody>
                    {result.rows.map((row, i) => (
                      <tr key={i} data-testid={`dx-row-${i}`} className="border-t border-slate-800 hover:bg-slate-800/40">
                        {result.columns.map((c) => <td key={c} className="px-3 py-2 whitespace-nowrap text-slate-200">{fmtVal(row[c])}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm text-slate-400">
                <span>Page {page}</span>
                <div className="flex gap-2">
                  <button data-testid="dx-prev" disabled={page <= 1} onClick={() => run(page - 1)} className="rounded-lg border border-slate-700 px-3 py-1.5 disabled:opacity-40">Prev</button>
                  <button data-testid="dx-next" disabled={!result.has_next} onClick={() => run(page + 1)} className="rounded-lg border border-slate-700 px-3 py-1.5 disabled:opacity-40">Next</button>
                </div>
              </div>
              {Object.keys(result.completeness || {}).length > 0 && (
                <div className="mt-2 text-xs text-amber-300/80">Incomplete on this page: {Object.entries(result.completeness).map(([c, v]) => `${c} (${v.null_in_page})`).join(", ")}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
